import { compact } from 'lodash';
import { removeFromArray } from '../../scripts/utils';
import { isRosreestrFeatureEnabled } from './ad-state.service';
import { isDealRent } from './rent-deal.service';
import { getUserTariff } from '../../../../shared/utils';
import { getIsNewPublishTermsEnabled } from '../../../../shared/utils/publishTerms';
import { selectIsAddJKState } from '../../../../shared/selectors/jk';
import { selectIsByHomeowner, selectIsPublishedOffer } from '../../../../shared/selectors/offer';
import { selectConfigValue } from '../../../../shared/selectors/config';
import { selectCanPublishAsHomeowner, selectIsHomeowner } from '../../../../shared/selectors/user';

export default function AdSegmentsService (
  userService,
  addFormLayoutService,
  jkService,
  store,
  authService,
  addFormService,
  config,
) {
  'ngInject';

  const hiddenSegments = [];

  const setSegmentVisible = (segment, value) => {
    const segmentIndex = hiddenSegments.indexOf(segment);
    if (segmentIndex !== -1 && value) {
      hiddenSegments.splice(segmentIndex, 1);
    }
    if (segmentIndex === -1 && !value) {
      hiddenSegments.push(segment);
    }
  };

  const isSegmentHidden = segment => hiddenSegments.includes(segment);

  const getSegmentsByCategory = (objectType, rentDuration, coworkingOfferType) => {
    if (rentDuration === 'short') {
      // Посуточная аренда
      if (objectType === 'house') {
        return ['about-object', 'description-and-photos', 'price-and-deal-terms'];
      } else {
        return ['about-object', 'about-building', 'homeowner', 'description-and-photos', 'price-and-deal-terms'];
      }
    } else if (objectType === 'newBuildingFlat') {
      // Новостройки
      const { isAgentCplModerationEnabled } = window.addFormState;
      const { user } = store.getState();
      const isCplNewbuilding = addFormService.isCplNewbuildingSelected();
      const isAuthUser = userService.isAuthenticated;
      const { isBuilder, isNonCommercial } = getUserTariff(user);

      const canShowGetAdditionalBenefits = isAgentCplModerationEnabled && isCplNewbuilding && (isNonCommercial || !isAuthUser || (isAuthUser && !isBuilder));
      const getAdditionalBenefitsSegment = canShowGetAdditionalBenefits ? 'get-additional-benefits' : null;

      return ['about-building', 'about-object', 'description-and-photos', getAdditionalBenefitsSegment, 'price-and-deal-terms'].filter(Boolean);
    } else if (['house', 'townhouse', 'cottage', 'houseShare', 'land', 'commercialLand'].includes(objectType)) {
      // Дом / дача / коттедж / доля в доме / участок / коммерческая земля
      return ['about-object', 'description-and-photos', 'price-and-deal-terms'];
    } else if (objectType === 'coworking' && coworkingOfferType) {
      return ['about-object', 'amenities', 'description-and-photos'];
    } if (['office', 'warehouse', 'industry', 'shoppingArea', 'garage'].includes(objectType)) {
      // Коммерческая недвижимость: офис, торговая площадь, гараж
      return ['about-object', 'about-building', 'infrastructure', 'description-and-photos', 'price-and-deal-terms'];
    } else if (objectType === 'business') {
      // Готовый бизнес
      return ['about-object', 'infrastructure', 'description-and-photos', 'price-and-deal-terms'];
    } else if (objectType === 'building') {
      // Здание, склад, производство
      return ['about-building', 'infrastructure', 'description-and-photos', 'price-and-deal-terms'];
    }

    // Основной порядок блоков
    return compact([
      'about-object',
      'about-building',
      'infrastructure',
      'homeowner',
      'description-and-photos',
      'price-and-deal-terms',
    ]);
  };

  // Получить список сегментов в зависимости от авторизации, категории, режима форма (короткая или полная) и др.
  const getSegments = (params) => {
    const {
      objectType,
      rentDuration,
      ignoreShortForm = false,
      showIndexingDelayAlert = false,
      showRentHelperTrap = false,
      showHomeownerWarning = false,
    } = params;

    const isShortForm = ignoreShortForm ? false : addFormLayoutService.isShortForm;

    const state = store.getState();
    const { abExperiments } = state;
    const { search } = window.location;

    const segments = [];

    if (!userService.isAuthenticated && !authService.isLastStepLogin) {
      segments.push('auth');
    } else {
      const isPublishedOffer = selectIsPublishedOffer(state);
      const isHomeowner = selectIsHomeowner(state);
      const isByHomeowner = selectIsByHomeowner(state);
      const canPublishAsHomeowner = selectCanPublishAsHomeowner(state);
      const isHomeownersCountPublishedOnly = selectConfigValue('homeownersCountPublishedOnly', state);

      // Показываем для всех, кроме собственников, которым более не доступны публикации от собственника
      // Или это существующее объявление
      const isAddformEnabled = isHomeownersCountPublishedOnly // Только при включенном feature-toggle
        ? !isHomeowner || isPublishedOffer || !isByHomeowner || canPublishAsHomeowner
        : true;

      segments.push('ad-type');

      if (showRentHelperTrap) {
        segments.push('rent-helper');
      }

      if (objectType && !showRentHelperTrap) {
        segments.push('address');

        if (addFormLayoutService.isShortLayout) {
          segments.push('short-form-switcher');
        }

        if (jkService.showJkForm()) {
          segments.push('about-building', 'ad-submit-jk');
        } else if (isShortForm && !addFormLayoutService.isShortFormAvailable()) {
          segments.push('new-building-form');
        } else {
          const { coworkingOfferType, isByHomeowner } = addFormService.ad;

          Array.prototype.push.apply(segments, getSegmentsByCategory(objectType, rentDuration, coworkingOfferType));

          if (isShortForm) {
            removeFromArray(segments, 'about-building');
          }

          if (isRosreestrFeatureEnabled && isByHomeowner) {
            removeFromArray(segments, 'cadastral-number-segment');
          } else {
            removeFromArray(segments, 'homeowner');
          }

          if (!userService.isAuthenticated && authService.isLastStepLogin) {
            segments.push('auth-last-step');
          } else {
            if (userService.verifiedPhoneNumberInCurrentSession) {
              segments.push('phone-verify', 'contact-info');
            } else if (!userService.user.hasVerifiedPhoneNumber) {
              segments.push('phone-verify');
            } else {
              segments.push('contact-info');
            }

            if (!isDealRent()) {
              segments.push('ad-placing-terms');
            }

            if (showIndexingDelayAlert) {
              segments.push('indexing-delay-alert');
            }

            if (showHomeownerWarning) {
              segments.push('homeowner-warning');
            }

            if (isAddformEnabled) {
              const isAddJKState = selectIsAddJKState(state);

              if (getIsNewPublishTermsEnabled({ config, abExperiments, search, isAddJKState })) {
                segments.push('summary');
              } else {
                segments.push('ad-submit-buttons');
              }
            } else {
              segments.push('disabled-warning');
            }
          }
        }
      }
    }

    return segments;
  };

  return {
    setSegmentVisible,
    isSegmentHidden,
    getSegments,
  };
};
